import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '../../toasts';
import relWarrantyAssetService from '../../services/relWarrantyAssets.service';
import { getError, showCurrency } from '../../utils';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { Section, StatStrip, DataTable, IdChip, RowActionsMenu, Button } from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';

function formatMoney(currency, amount) {
	if (amount === null || amount === undefined) {
		return '';
	}
	return `${showCurrency(currency) || ''} ${Number(amount).toLocaleString('es-UY')}`.trim();
}

// A cash ('efectivo') warranty has no debt/asset, so its currency lives on the
// warranty itself; the other two options carry it on the guaranteed record.
const warrantyCurrency = (warranty) =>
	warranty?.debt?.currency || warranty?.asset?.currency || warranty?.currency || null;

function CreditWarranties({ creditId, rows = [], loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const location = useLocation();
	const [deletingWarranty, setDeletingWarranty] = useState(null);

	const openWarrantyEntity = (relWarranty) => {
		const debtId = relWarranty.warranty?.debt?._id;
		const assetId = relWarranty.warranty?.asset?._id;
		if (debtId) navigate(`/AdminScreen/debts/debt/${debtId}`);
		else if (assetId) navigate(`/AdminScreen/assets/asset/${assetId}`);
	};

	const handleCreateWarranty = () => {
		const returnTo = encodeURIComponent(location.pathname + location.search);
		navigate(`/AdminScreen/credits/credit/${creditId}/newWarranty?returnTo=${returnTo}`);
	};

	const stats = useMemo(() => {
		const totalByCurrency = new Map();
		rows.forEach((relWarranty) => {
			const key = warrantyCurrency(relWarranty.warranty) || '—';
			totalByCurrency.set(key, (totalByCurrency.get(key) || 0) + (relWarranty.warranty?.amount || 0));
		});
		return [
			{ label: t('entity.warranty.plural'), value: rows.length },
			{
				label: t('field.totalGuaranteed'),
				value:
					[...totalByCurrency.entries()].map(([currency, amount]) => formatMoney(currency, amount)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows, t]);

	async function handleConfirmDelete() {
		try {
			await relWarrantyAssetService.delete(deletingWarranty._id);
			toast.success(t('msg.warrantyDeleted'));
			onRefetch();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setDeletingWarranty(null);
		}
	}

	const columns = [
		{ key: 'option', header: t('field.option'), render: (relWarranty) => relWarranty.warranty?.option },
		{
			key: 'debt',
			header: t('entity.debt'),
			render: (relWarranty) =>
				relWarranty.warranty?.debt?.customId ? <IdChip>{relWarranty.warranty.debt.customId}</IdChip> : null,
		},
		{
			key: 'asset',
			header: t('entity.asset'),
			render: (relWarranty) =>
				relWarranty.warranty?.asset?.customId ? <IdChip>{relWarranty.warranty.asset.customId}</IdChip> : null,
		},
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (relWarranty) => (
				<span className='tw:font-mono tw:tabular-nums'>
					{formatMoney(warrantyCurrency(relWarranty.warranty), relWarranty.warranty?.amount)}
				</span>
			),
		},
	];

	const renderRowActions = (relWarranty) => (
		<RowActionsMenu
			items={[{ label: t('action.delete'), onClick: () => setDeletingWarranty(relWarranty), danger: true }]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end'>
				<Button size='sm' variant='primary' onClick={handleCreateWarranty}>
					{t('title.newWarranty')}
				</Button>
			</div>
			<Section title={t('entity.warranty.plural')}>
				<DataTable
					columns={columns}
					rows={rows}
					loading={loading}
					error={error}
					onRowClick={(relWarranty) => openWarrantyEntity(relWarranty)}
					rowActions={renderRowActions}
					emptyMessage='No se encontraron garantías para este crédito.'
				/>
			</Section>

			<ConfirmationModal
				show={Boolean(deletingWarranty)}
				handleClose={() => setDeletingWarranty(null)}
				handleConfirm={handleConfirmDelete}
				title={`Eliminar ${t('entity.warranty')}`}
				description={t('msg.confirmDeleteWarranty')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default CreditWarranties;
